import {
  BedDouble,
  Users,
  Waves,
  Wind,
  ChefHat,
  Bath,
  Sofa,
  Tv,
  DoorOpen,
  Mountain,
} from "lucide-react";
import { useReveal } from "../hooks/useReveal";
import { useLanguage } from "../lib/LanguageContext";

export const Apartments = () => {
  const ref = useReveal();
  const { t } = useLanguage();

  const types = [
    {
      name: t("apts_type_1"),
      guests: t("apts_guests_3"),
      beds: [
        `${t("apts_bed_full")} · ${t("apts_bed_bedroom")}`,
        `${t("apts_bed_sofa")} · ${t("apts_bed_living")}`,
      ],
      view: t("apts_view_garden"),
      floor: t("apts_floor_ground"),
      price: "€65",
      priceNote: t("apts_per_night"),
      accent: "#1a5b73",
    },
    {
      name: t("apts_type_2"),
      guests: t("apts_guests_5"),
      beds: [
        `2 ${t("apts_bed_twin")} · ${t("apts_bed_bedroom_1")}`,
        `${t("apts_bed_full")} · ${t("apts_bed_bedroom_2")}`,
        `${t("apts_bed_sofa")} · ${t("apts_bed_living")}`,
      ],
      view: t("apts_view_garden"),
      floor: t("apts_floor_ground"),
      price: "€85",
      priceNote: t("apts_per_night"),
      accent: "#3a7d90",
      featured: true,
    },
    {
      name: t("apts_type_3"),
      guests: t("apts_guests_5"),
      beds: [
        `${t("apts_bed_full")} · ${t("apts_bed_bedroom_1")}`,
        `2 ${t("apts_bed_twin")} · ${t("apts_bed_bedroom_2")}`,
        `${t("apts_bed_sofa")} · ${t("apts_bed_living")}`,
      ],
      view: t("apts_view_sea"),
      price: "€95",
      priceNote: t("apts_per_night"),
      accent: "#1a5b73",
    },
  ];

  const inApartment = [
    { icon: Wind, label: t("apts_inc_ac") },
    { icon: ChefHat, label: t("apts_inc_kitchen") },
    { icon: Bath, label: t("apts_inc_bath") },
    { icon: Mountain, label: t("apts_inc_balcony") },
    { icon: Sofa, label: t("apts_inc_sofa") },
    { icon: Tv, label: t("apts_inc_tv") },
    { icon: DoorOpen, label: t("apts_inc_entrance") },
    { icon: Waves, label: t("apts_inc_pool") },
  ];

  return (
    <section
      id="apartments"
      data-testid="apartments-section"
      className="py-24 md:py-36 bg-[#f3efe6]"
    >
      <div ref={ref} className="reveal max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#6b705c] mb-6">
              {t("apts_eyebrow")}
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#0a192f] leading-[1.02]">
              {t("apts_title_a")}
              <br />
              {t("apts_title_b")}{" "}
              <em className="italic text-[#1a5b73]">{t("apts_title_c")}</em>{" "}
              {t("apts_title_d")}
            </h2>
          </div>
          <p className="md:max-w-sm text-[#4a4a4a] font-light leading-relaxed">
            {t("apts_intro")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-6">
          {types.map((tp, i) => (
            <div
              key={i}
              data-testid={`apartment-type-${i}`}
              className={`relative bg-white border border-[#0a192f]/10 rounded-3xl p-8 md:p-10 flex flex-col hover:shadow-2xl transition-shadow duration-500 ${
                tp.featured ? "md:-translate-y-4" : ""
              }`}
            >
              {tp.featured && (
                <div className="absolute -top-3 left-8 bg-[#1a5b73] text-white text-[10px] uppercase tracking-[0.24em] px-4 py-1.5 rounded-full">
                  {t("apts_most_popular")}
                </div>
              )}
              <div
                className="inline-flex h-12 w-12 items-center justify-center rounded-full mb-6"
                style={{ background: `${tp.accent}14`, color: tp.accent }}
              >
                <BedDouble className="h-5 w-5" strokeWidth={1.4} />
              </div>
              <h3 className="font-serif text-2xl md:text-3xl font-medium text-[#0a192f] leading-snug">
                {tp.name}
              </h3>
              <div className="mt-3 flex items-center gap-2 text-[#6b705c] text-sm">
                <Users className="h-4 w-4" strokeWidth={1.4} />
                {tp.guests}
              </div>
              {tp.floor && (
                <div className="mt-2 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#1a5b73]">
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: tp.accent }}
                  />
                  {tp.floor}
                </div>
              )}
              <ul className="mt-6 space-y-2 text-[#4a4a4a] text-sm md:text-[15px] font-light border-t border-[#0a192f]/10 pt-5">
                {tp.beds.map((b, j) => (
                  <li
                    key={j}
                    className="flex items-start gap-2 leading-relaxed"
                  >
                    <span
                      className="mt-2 h-1 w-1 rounded-full flex-shrink-0"
                      style={{ background: tp.accent }}
                    />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-8">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#6b705c] mb-2">
                  {t("apts_view_label")}
                </div>
                <div className="text-[#0a192f] font-serif text-lg">
                  {tp.view}
                </div>

                <div className="mt-6 pt-5 border-t border-[#0a192f]/10 flex items-baseline justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#6b705c] mb-1">
                      {t("apts_price_label")}
                    </div>
                    <div
                      className="font-serif text-2xl md:text-3xl font-medium"
                      style={{ color: tp.accent }}
                    >
                      {tp.price}
                    </div>
                  </div>
                  <div className="text-xs text-[#6b705c]">{tp.priceNote}</div>
                </div>

                <a
                  href="#contact"
                  data-testid={`apartment-type-${i}-cta`}
                  className="mt-6 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[#1a5b73] hover:text-[#0a192f] transition-colors"
                >
                  {t("apts_cta")}
                  <span aria-hidden>→</span>
                </a>
              </div>
            </div>
          ))}
        </div>

        <div className="mb-20 text-center text-xs text-[#6b705c]">
          {t("apts_pricing_note")}
        </div>

        <div className="border-t border-[#0a192f]/10 pt-12">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#6b705c] mb-8">
            {t("apts_includes")}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-5">
            {inApartment.map((f, i) => (
              <div
                key={i}
                data-testid={`apartment-feature-${i}`}
                className="flex items-center gap-3 text-[#0a192f]"
              >
                <f.icon
                  className="h-4 w-4 text-[#1a5b73] flex-shrink-0"
                  strokeWidth={1.4}
                />
                <span className="text-sm md:text-[15px]">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
